// Helpers to compare record/draft field values

// Fields that make up a record's content
export const RECORD_FIELDS = [
  "ofid",
  "fieldName",
  "dataType",
  "level",
  "tags",
  "linkReference",
  "standardRefs",
  "introduced",
  "deprecated",
  "values",
  "example",
  "description",
  "uploadedFile",
];

// Normalise tags (populated objects or IDs) to a sorted list of ID strings
const toTagIds = (tags) =>
  (tags || [])
    .map((t) => (typeof t === "object" ? t._id : t).toString())
    .sort();

// Text fields of a standard reference
const STANDARD_REF_FIELDS = ["externalId", "comment"];

// Normalise standard references (standard populated or ID) to plain objects
// with the standard ID and only the non-empty, trimmed text fields
export const normalizeStandardRefs = (refs) =>
  (refs || []).map((ref) => {
    const standard =
      ref.standard && typeof ref.standard === "object"
        ? ref.standard._id
        : ref.standard;
    const normalized = { standard: standard ? standard.toString() : "" };
    for (const key of STANDARD_REF_FIELDS) {
      const value = (ref[key] ?? "").toString().trim();
      if (value) normalized[key] = value;
    }
    return normalized;
  });

// Comparable key of a single standard reference
export const standardRefKey = (ref) =>
  JSON.stringify(normalizeStandardRefs([ref])[0]);

// True if two values of the given field differ
export const valuesDiffer = (fieldName, a, b) => {
  // Tags: compare as sets of IDs (order-insensitive)
  if (fieldName === "tags") {
    return JSON.stringify(toTagIds(a)) !== JSON.stringify(toTagIds(b));
  }
  // Standard references: compare as sets of normalised refs (order-insensitive)
  if (fieldName === "standardRefs") {
    const keys = (refs) => (refs || []).map(standardRefKey).sort();
    return JSON.stringify(keys(a)) !== JSON.stringify(keys(b));
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return JSON.stringify(a) !== JSON.stringify(b);
  }
  return String(a || "") !== String(b || "");
};

// True if two records have identical content in all record fields
export const recordsIdentical = (a, b) =>
  RECORD_FIELDS.every((name) => !valuesDiffer(name, a?.[name], b?.[name]));
